import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  TextField,
  Typography,
} from '@mui/material';

const DIGIT_COUNT = 6;

type WithdrawTotpDialogProps = {
  open: boolean;
  loading?: boolean;
  onClose: () => void;
  onSubmit: (code: string) => void;
};

/** Second-step modal: 6-digit TOTP or recovery code for merchant withdraw. */
export function WithdrawTotpDialog({
  open,
  loading = false,
  onClose,
  onSubmit,
}: WithdrawTotpDialogProps) {
  const [digits, setDigits] = useState<string[]>(() =>
    Array.from({ length: DIGIT_COUNT }, () => ''),
  );
  const [recoveryMode, setRecoveryMode] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState('');
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const wasLoadingRef = useRef(false);

  const reset = useCallback(() => {
    setDigits(Array.from({ length: DIGIT_COUNT }, () => ''));
    setRecoveryMode(false);
    setRecoveryCode('');
  }, []);

  useEffect(() => {
    if (wasLoadingRef.current && !loading && open) {
      reset();
      window.setTimeout(() => inputRefs.current[0]?.focus(), 0);
    }
    wasLoadingRef.current = loading;
  }, [loading, open, reset]);

  useEffect(() => {
    if (!open) {
      reset();
      return;
    }
    const t = window.setTimeout(() => {
      if (recoveryMode) {
        return;
      }
      inputRefs.current[0]?.focus();
    }, 0);
    return () => window.clearTimeout(t);
  }, [open, recoveryMode, reset]);

  const submitDigits = (next: string[]) => {
    if (next.some((d) => d === '')) {
      return;
    }
    onSubmit(next.join(''));
  };

  const applyDigitString = (raw: string, startIndex: number) => {
    const only = raw.replace(/\D/g, '');
    if (!only) {
      return;
    }
    setDigits((prev) => {
      const next = [...prev];
      for (let i = 0; i < only.length && startIndex + i < DIGIT_COUNT; i += 1) {
        next[startIndex + i] = only[i]!;
      }
      const focusIndex = Math.min(startIndex + only.length, DIGIT_COUNT - 1);
      inputRefs.current[focusIndex]?.focus();
      if (next.every((d) => d !== '')) {
        submitDigits(next);
      }
      return next;
    });
  };

  const handleDigitChange = (index: number, value: string) => {
    if (value.length > 1) {
      applyDigitString(value, index);
      return;
    }
    if (value !== '' && !/^\d$/.test(value)) {
      return;
    }
    setDigits((prev) => {
      const next = [...prev];
      next[index] = value;
      if (value && index < DIGIT_COUNT - 1) {
        inputRefs.current[index + 1]?.focus();
      }
      if (value && index === DIGIT_COUNT - 1 && next.every((d) => d !== '')) {
        submitDigits(next);
      }
      return next;
    });
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (
      e.key === 'Enter' &&
      !e.repeat &&
      digits.every((d) => d !== '') &&
      !loading
    ) {
      submitDigits(digits);
    }
  };

  const handleConfirm = () => {
    if (recoveryMode) {
      const code = recoveryCode.trim();
      if (!code) {
        return;
      }
      onSubmit(code);
      return;
    }
    submitDigits(digits);
  };

  const canConfirm = recoveryMode
    ? recoveryCode.trim().length > 0
    : digits.every((d) => d !== '');

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>Enter 2FA Code</DialogTitle>
      <DialogContent>
        {recoveryMode ? (
          <TextField
            label="Recovery code"
            value={recoveryCode}
            onChange={(e) => setRecoveryCode(e.target.value)}
            fullWidth
            size="small"
            autoFocus
            autoComplete="one-time-code"
            disabled={loading}
            sx={{ mt: 1 }}
          />
        ) : (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 1,
              mt: 1,
              mb: 1,
            }}
          >
            {digits.map((digit, index) => (
              <TextField
                key={index}
                inputRef={(el) => {
                  inputRefs.current[index] = el;
                }}
                variant="outlined"
                disabled={loading}
                value={digit}
                onChange={(e) => handleDigitChange(index, e.target.value)}
                onKeyDown={(e) =>
                  handleKeyDown(e as React.KeyboardEvent<HTMLInputElement>, index)
                }
                inputProps={{
                  maxLength: 6,
                  inputMode: 'numeric',
                  autoComplete: index === 0 ? 'one-time-code' : 'off',
                  style: { textAlign: 'center', padding: '12px 0' },
                }}
                sx={{
                  width: 44,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
            ))}
          </Box>
        )}
        <Typography variant="body2" align="center" sx={{ mt: 1 }}>
          <Link
            component="button"
            type="button"
            variant="body2"
            underline="hover"
            disabled={loading}
            onClick={() => {
              setRecoveryMode((prev) => !prev);
              setRecoveryCode('');
              setDigits(Array.from({ length: DIGIT_COUNT }, () => ''));
            }}
          >
            {recoveryMode ? 'Use authenticator code' : 'Use recovery code instead'}
          </Link>
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!canConfirm || loading}
        >
          {loading ? 'Withdrawing...' : 'Confirm withdraw'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
